import { useNavigate } from "react-router";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../../../stores/hooks/hooks";
import { useEffect, useMemo, useState } from "react";
import { useFlags } from "launchdarkly-react-client-sdk";
import {
  deleteCampaignByIdData,
  fetchCampaignByIdData,
  fetchInitialRecommendation,
} from "../../../../../../stores/thunks/campaignsThunk";
import { clearCampaignById } from "../../../../../../stores/slice/campaignsSlice";
import {
  RevButton,
  RevDialog,
  RevSlideout,
  RevIconButton,
  RevSpinner,
} from "@revmatics/catalyst";
import { faPencil, faTrash, faXmark } from "@fortawesome/pro-light-svg-icons";
import { CreateEditDetailCampaign } from "./components/createEditDetailCampaign/CreateEditDetailCampaign.tsx";
import { NotFoundPage } from "../../../../../../pages/notFound/NotFoundPage.tsx";

type Props = {
  campaignId: string | null;
  setIsOpen: () => void;
};

export const CampaignWrapper = ({ campaignId, setIsOpen }: Props) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const isLoadingById = useAppSelector(
    (state) => state.campaigns.isLoadingById,
  );
  const isLoadingDelete = useAppSelector(
    (state) => state.campaigns.isLoadingDelete,
  );
  const isLoading = useAppSelector((state) => state.campaigns.isLoading);
  const currentBrand = useAppSelector((state) => state.brand.currentBrand);
  const campaignById = useAppSelector((state) => state.campaigns.campaignById);
  const initRecommendation = useAppSelector(
    (state) => state.campaigns.initRecommendation,
  );
  const [isEditMode, setIsEditMode] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const {
    detailsCampaign: ffDetailsCampaign,
    createCampaign: ffCreateCampaign,
    deleteCampaign: ffDeleteCampaign,
    editCampaigns: ffEditCampaign,
  } = useFlags();

  useEffect(() => {
    if (
      campaignId &&
      campaignId !== "create" &&
      (!campaignById || campaignById.id.toString() !== campaignId)
    ) {
      dispatch(fetchCampaignByIdData(campaignId));
    }
  }, [dispatch, campaignId, campaignById, currentBrand]);

  useEffect(() => {
    if (!initRecommendation) {
      dispatch(fetchInitialRecommendation());
    }
  }, [dispatch, initRecommendation]);

  useEffect(() => {
    if (!campaignId) {
      setIsEditMode(false);
      dispatch(clearCampaignById());
    }
  }, [campaignId, dispatch]);

  const displayContent = useMemo(() => {
    if (campaignId === "create") {
      return initRecommendation && ffCreateCampaign;
    } else {
      return campaignById && initRecommendation && ffDetailsCampaign;
    }
  }, [
    campaignById,
    campaignId,
    ffCreateCampaign,
    ffDetailsCampaign,
    initRecommendation,
  ]);

  const displayNotFound = useMemo(() => {
    if (campaignId === "create") {
      return !ffCreateCampaign || !initRecommendation;
    } else {
      return (
        (!campaignById && !isLoadingById) ||
        !ffDetailsCampaign ||
        !initRecommendation
      );
    }
  }, [
    campaignById,
    campaignId,
    ffCreateCampaign,
    ffDetailsCampaign,
    initRecommendation,
    isLoadingById,
  ]);

  const handleDelete = () => {
    if (campaignById) {
      dispatch(
        deleteCampaignByIdData({
          id: campaignById.id.toString(),
          name: campaignById.name,
          callback: () => navigate("/campaigns"),
        }),
      );
    }
  };

  return (
    <RevSlideout
      isOpen={!!campaignId}
      setIsOpen={setIsOpen}
      size="lg"
      headerComponent={
        <div className="flex justify-between px-4 py-6 sm:px-6 border-b border-monochrome-100">
          <h5>
            {campaignId === "create" ? "Create Campaign" : "Campaign Details"}
          </h5>
          <div className="flex gap-2 items-center">
            {ffDeleteCampaign && !isEditMode && campaignId !== "create" && (
              <RevIconButton
                variant="outline"
                size="l"
                icon={faTrash}
                handleClick={() => setIsDeleteDialogOpen(true)}
                ariaLabel="Delete campaign slideout"
                data-testid="delete-btn-campaign-details"
              />
            )}
            {ffEditCampaign && !isEditMode && campaignId !== "create" && (
              <RevIconButton
                variant="outline"
                size="l"
                icon={faPencil}
                handleClick={() => setIsEditMode(true)}
                ariaLabel="Edit campaign slideout"
                data-testid="edit-btn-campaign-details"
              />
            )}
            <RevIconButton
              icon={faXmark}
              variant="outline"
              size="l"
              handleClick={setIsOpen}
              ariaLabel="Close slideout"
            />
          </div>
        </div>
      }
    >
      {(isLoadingById || isLoading) && (
        <div className="h-full">
          <RevSpinner size="large" color="primary" centered />
        </div>
      )}
      {(campaignId === "create" && !ffCreateCampaign) ||
        (campaignId && campaignId !== "create" && !ffDetailsCampaign && (
          <NotFoundPage />
        ))}
      {displayContent && (
        <CreateEditDetailCampaign
          campaignById={campaignById}
          initRecommendation={initRecommendation}
          isEditMode={isEditMode}
          setIsEditMode={setIsEditMode}
          create={campaignId === "create"}
        />
      )}
      {displayNotFound && <NotFoundPage />}
      <RevDialog
        isOpen={isDeleteDialogOpen}
        setIsOpen={setIsDeleteDialogOpen}
        title="Delete Campaign?"
        classNameContainer="z-50"
        btnList={[
          <RevButton
            key="cancel-campaign-delete"
            text="Cancel"
            variant="secondary"
            handleClick={() => setIsDeleteDialogOpen(false)}
          />,
          <RevButton
            key="delete-campaign-delete"
            text="Yes, delete"
            variant="primary"
            buttonColor="error"
            handleClick={handleDelete}
            isLoading={isLoadingDelete}
          />,
        ]}
      >
        <p className="typography-body-sm text-monochrome-900">
          Are you sure you want to delete the campaign "{campaignById?.name}
          "? This action can't be undone.
        </p>
      </RevDialog>
    </RevSlideout>
  );
};
